import API from './API';

// -----------------------------------------------------------------------------
//  Partner — caricamento a batch
//
//  `getPartners()` (in fondo) scarica l'elenco intero: oltre il tetto
//  del backend (DEFAULT_CRUD_LIMIT=1000) i partner sparirebbero senza errore.
//  Le funzioni nuove chiedono una pagina per volta e lasciano filtri e
//  ordinamento al database.
//
//  TIPO PARTNER — a schema è il booleano `is_restaurant`:
//    false → automotive   true → notturno (ex "restaurant")
//
//  DUE CHIAVI DIVERSE, da non confondere (lo dice anche l'entity):
//    - `API_key` + `domain` + `alias` = credenziali di pagamento **Nexi**.
//      Sono in chiaro ma `@Exclude()`: NON tornano dalle GET CRUD, si leggono
//      solo da `GET /partner/info-payment/:id`.
//    - `api_access_key_hash` = accesso API machine-to-machine. Il server
//      genera una chiave `sk_simba_…` e ne salva solo l'hash: il testo in
//      chiaro esiste una volta sola, nella risposta di `POST …/api-access`.
//
//  ⚠️ PATCH /partner: a un utente con ruolo **tenant** il backend tiene solo le
//  colonne `partner_*` e scarta tutto il resto **in silenzio** (nessun 403).
//  Le pagine devono quindi mostrare l'anagrafica in sola lettura a chi non è
//  globale, invece di far salvare nel vuoto: usare `canEditAnagrafica()`.
// -----------------------------------------------------------------------------

const PAGE_SIZE = 25;

// `headquarters.area` è la vecchia zona (in dismissione); la copertura driver
// e il planner ragionano per PROVINCIA della sede.
const LIST_JOIN = ['headquarters', 'headquarters.area', 'headquarters.province_ref'];

// Ruoli che il backend considera globali (Auth/roles.decorator.ts).
const GLOBAL_ROLES = ['superadmin'];

function normalize(response, fallbackPage = 1) {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { rows: body, total: body.length, page: fallbackPage, pageCount: 1 };
  }
  return {
    rows: body?.data ?? [],
    total: body?.total ?? 0,
    page: body?.page ?? fallbackPage,
    pageCount: body?.pageCount ?? 1,
  };
}

function searchClause(term) {
  const t = (term || '').trim();
  if (!t) return null;
  return {
    $or: [
      { company_name: { $contL: t } },
      { piva: { $contL: t } },
      { mail: { $contL: t } },
      { phone_number: { $contL: t } },
    ],
  };
}

// `type`: 'automotive' | 'notturno' | '' (tutti)
function typeClause(type) {
  if (type === 'automotive') return { is_restaurant: { $eq: false } };
  if (type === 'notturno') return { is_restaurant: { $eq: true } };
  return null;
}

export default {
  // ------------------------------------------------------------ RUOLI ---
  canEditAnagrafica(role) {
    return GLOBAL_ROLES.includes(role);
  },

  isNotturno(partner) {
    return partner?.is_restaurant === true;
  },

  // ------------------------------------------------------------ LISTA ---
  getPartnersPaged({
    page = 1,
    limit = PAGE_SIZE,
    search = '',
    type = '',
    sort = '',
  } = {}) {
    const and = [];
    const t = typeClause(type);
    if (t) and.push(t);
    const s = searchClause(search);
    if (s) and.push(s);

    const params = {
      page,
      limit,
      sort: sort || 'company_name,ASC',
      join: LIST_JOIN,
    };
    if (and.length) params.s = JSON.stringify({ $and: and });

    return API.get('/partner', { params }).then((r) => normalize(r, page));
  },

  countPartners(type) {
    const params = { page: 1, limit: 1 };
    const t = typeClause(type);
    if (t) params.s = JSON.stringify({ $and: [t] });
    return API.get('/partner', { params }).then((r) => normalize(r).total);
  },

  // -------------------------------------------------------- DETTAGLIO ---
  getPartnerDetail(id) {
    return API.get(`/partner/${id}`, { params: { join: LIST_JOIN } });
  },

  // I dipendenti NON arrivano col partner: il join `headquarters.employees`
  // è commentato nel controller. Si caricano a parte, passando dalle sedi.
  getEmployeesByPartner(partnerId) {
    return API.get('/employee', {
      params: {
        join: ['headquarters', 'headquarters.partner'],
        // I dipendenti eliminati restano nel database (lo storico li cita),
        // ma non sono più del partner.
        s: JSON.stringify({
          $and: [{ 'headquarters.partner.id': { $eq: partnerId } }, { is_deleted: { $eq: false } }],
        }),
        sort: 'surname,ASC',
      },
    }).then((r) => normalize(r).rows);
  },

  // ----------------------------------------------------- PAGAMENTI NEXI ---
  // Unico modo di rileggere API_key/domain/alias (esclusi dalle GET CRUD).
  getPaymentInfo(partnerId) {
    return API.get(`/partner/info-payment/${partnerId}`).then((r) => r.data);
  },

  // ------------------------------------------------------- ACCESSO API ---
  // Stato soltanto: la chiave in chiaro non è più recuperabile.
  getApiAccessStatus(partnerId) {
    return API.get(`/partner/${partnerId}/api-access`).then((r) => r.data);
  },

  // Restituisce { api_key } in chiaro: è l'ULTIMA volta che si può leggere.
  issueApiKey(partnerId) {
    return API.post(`/partner/${partnerId}/api-access`).then((r) => r.data);
  },

  revokeApiKey(partnerId) {
    return API.delete(`/partner/${partnerId}/api-access`);
  },

  // ------------------------------------------------------------ FASCE ---
  // Le fasce non si modificano: si CHIUDE quella in corso (valid_to) e se ne
  // crea una nuova, così i movimenti già confermati tengono il prezzo di
  // allora (snapshot in route.applied_price). Scrittura: solo superadmin.
  // ⚠️ Si chiedono solo le fasce ANCORA VALIDE. Le chiuse non si cancellano
  // mai (è la storicità: i movimenti confermati tengono il prezzo di allora),
  // quindi a ogni salvataggio del listino la tabella cresce di una trentina di
  // righe. Con il vecchio tetto di 200 righe e nessun filtro, dopo qualche
  // salvataggio il server ne restituiva 200 a caso: le fasce valide che
  // finivano oltre non arrivavano alla pagina — sparivano dalla tabella e il
  // salvataggio dopo non le chiudeva nemmeno, perché non sapeva che c'erano.
  getBandsByPartner(partnerId, { includeGlobal = true } = {}) {
    const or = [{ 'partner.id': { $eq: partnerId } }];
    if (includeGlobal) or.push({ 'partner.id': { $isnull: true } });
    const oggi = new Date().toISOString().slice(0, 10);
    return API.get('/tariff-band', {
      params: {
        join: ['partner'],
        s: JSON.stringify({
          $and: [
            { $or: or },
            { $or: [{ valid_to: { $isnull: true } }, { valid_to: { $gte: oggi } }] },
          ],
        }),
        sort: 'service,ASC',
        limit: 2000, // tetto del backend (MAX_CRUD_LIMIT)
      },
    }).then((r) => normalize(r).rows);
  },

  createBand(band) {
    return API.post('/tariff-band', band);
  },

  // Tutte le fasce di un listino in UNA richiesta.
  //
  // ⚠️ Un listino completo sono ~60 fasce (15 scaglioni × 1 e 2 operatori ×
  // tariffa cliente e quota partner). Creandole una per una il browser
  // spediva 60 POST più 60 preflight CORS, a sei connessioni per volta: in
  // locale sono tre secondi, verso il server di produzione il salvataggio
  // restava a girare. Il CRUD ha la rotta `bulk` apposta.
  createBands(bands) {
    return API.post('/tariff-band/bulk', { bulk: bands }, { timeout: 60000 });
  },

  // Chiusura di una fascia: si scrive solo `valid_to`.
  closeBand(bandId, validTo) {
    return API.patch(`/tariff-band/${bandId}`, { valid_to: validTo }, { timeout: 60000 });
  },

  // Fasce VALET: tabella dedicata (`valet_km_band`), separata da `tariff_band`.
  // I km oltre quelli compresi nel pacchetto si pagano al €/km della fascia
  // (`excess_price_per_km`), non con le tariffe spostamenti.
  getValetBandsByPartner(partnerId, { includeGlobal = true } = {}) {
    const or = [{ 'partner.id': { $eq: partnerId } }];
    if (includeGlobal) or.push({ 'partner.id': { $isnull: true } });
    return API.get('/valet-km-band', {
      params: {
        join: ['partner'],
        s: JSON.stringify({ $or: or }),
        sort: 'from_km,ASC',
        limit: 100,
      },
    }).then((r) => normalize(r).rows);
  },

  // L'associazione advisor↔partner è una M2M dichiarata sul solo Advisor:
  // dal partner non è raggiungibile, si interroga al contrario.
  getAdvisorsByPartner(partnerId) {
    return API.get('/advisor', {
      params: {
        join: ['partners'],
        s: JSON.stringify({ 'partners.id': { $eq: partnerId } }),
      },
    }).then((r) => normalize(r).rows);
  },

  createValetBand(band) {
    return API.post('/valet-km-band', band);
  },

  // Le fasce valet non hanno date di validità: si modificano sul posto. I
  // pacchetti già venduti non cambiano: prezzi e km compresi sono copiati lì.
  updateValetBand(bandId, fields) {
    return API.patch(`/valet-km-band/${bandId}`, fields);
  },

  // Rifiutata dal server se la fascia è già usata da un addebito o venduta in
  // un pacchetto (`booking.valet_band_id`, `minute.valet_band_id`, FK senza cascade).
  deleteValetBand(bandId) {
    return API.delete(`/valet-km-band/${bandId}`);
  },

  // Elenco advisor per la tendina in creazione.
  getAdvisors() {
    return API.get('/advisor', { params: { sort: 'surname,ASC', limit: 200 } })
      .then((r) => normalize(r).rows);
  },

  // L'associazione advisor↔partner è una M2M dichiarata sul solo Advisor:
  // si scrive quindi PATCHando l'ADVISOR, non il partner.
  linkAdvisorToPartner(advisorId, partnerIds) {
    return API.patch(`/advisor/${advisorId}`, {
      partners: partnerIds.map((id) => ({ id })),
    });
  },

  createPartner(partner) {
    return API.post('/partner', partner);
  },

  // Anteprima prezzo: { partnerId?, km, date } -> quote
  quote(payload) {
    return API.post('/tariff-band/quote', payload).then((r) => r.data);
  },

  getPartnerById(id) {
    return API.get(`/partner?filter=id||$eq||${id}&`, {
      params: {
        join: ['headquarters', 'headquarters.area'],
      },
    });
  },

  // Per il dialogo di modifica della sede: regione e provincia strutturate
  // non arrivano col partner (il suo controller non ammette quei join).
  getHeadquarterForEdit(id) {
    return API.get(`/headquarter/${id}`, {
      params: { join: ['area', 'region', 'province_ref'] },
    }).then((r) => r.data);
  },

  // Il partner-jolly dei servizi chiesti senza partner dall'app
  // (SIMBA MOBILITY). Serve a riconoscerlo: a lui si propongono i listini di
  // TUTTI i servizi, notturno compreso. Lo dice il backend, che lo risolve per
  // `SIMBA_MOBILITY_PARTNER_ID` o per nome: farlo qui per nome divergerebbe.
  // Niente avviso globale: un backend senza C10 prende `simba-mobility` per
  // l'id di `/partner/:id` e risponde 400 «Invalid param id». La pagina
  // funziona lo stesso, e l'errore finiva in un toast a ogni apertura.
  getSimbaMobility() {
    return API.get('/partner/simba-mobility', { skipErrorToast: true }).then((r) => r.data);
  },

  getEmployeeById(id) {
    return API.get(`/employee?filter=id||$eq||${id}&`, {
      params: {
        join: ['headquarters', 'headquarters.partner'],
      },
    });
  },

  updatePartner(partner) {
    return API.patch('/partner/' + partner.id, partner);
  },

  getPartners() {
    return API.get('/partner?sort=company_name,ASC');
  },

  updateHeadquarter(headquarter) {
    return API.patch('/headquarter/' + headquarter.id, headquarter);
  },

  updateEmployee(employee) {
    return API.patch('/employee/' + employee.id, employee);
  },

  // Elimina un dipendente, solo Simba: non entra più e la sua email si libera;
  // resta nello storico delle prenotazioni e degli spostamenti che ha gestito.
  deleteEmployee(id) {
    return API.delete('/employee/' + id);
  },

  addEmployee(employee) {
    return API.post('/auth/register/', employee);
  },

  addHeadquarter(headquarter) {
    return API.post('/headquarter/', headquarter);
  },

};
